import toastr from 'toastr'
import openSocket from 'socket.io-client'
import mixpanel from 'mixpanel-browser'
import mitt from 'mitt'

const UPDATES_API_BASE = import.meta.env.VITE_UPDATES_API_BASE

toastr.options = {
    "closeButton": false,
    "debug": false,
    "newestOnTop": false,
    "progressBar": false,
    "positionClass": "toast-top-right",
    "preventDuplicates": true,
    "onclick": null,
    "showDuration": "300",
    "hideDuration": "1000",
    "timeOut": "5000",
    "extendedTimeOut": "1000",
    "showEasing": "swing",
    "hideEasing": "linear",
    "showMethod": "fadeIn",
    "hideMethod": "fadeOut"
}

export default {
        updatesSocket: null,
        clientSocketConnected: false,
        clientRoomJoined: false,
        updatesClientId: null,
        emitter: mitt(),
        // https://v3-migration.vuejs.org/breaking-changes/events-api.html
        $emit(evt, obj) {
            this.emitter.emit(evt, obj)
        },
        $on(evt, fn) {
            this.emitter.on(evt, fn)
        },
        $off(evt, fn) {
            this.emitter.off(evt, fn)
        },
        noAccess() {
            this.$emit('no-access')
        },
        loggedOut() {
            this.$emit('logged-out')
        },
        sessionInvalid() {
            this.$emit('session-invalid')
        },
        // A setup-only session (MFA past its deadline): App.vue routes to setup
        mfaSetupRequired() {
            this.$emit('mfa-setup-required')
        },
        onNavigation() {
            this.$emit('navigation')
        },
        // Safely extract a single user-facing message from any caught value.
        // API errors are normalized to {__all__: [...]} by api.js, but raw JS
        // exceptions thrown inside .then() handlers (or other non-API
        // rejections) have no __all__ — reading errors.__all__[0] on those
        // throws a second error that masks the first. Use this in .catch blocks
        // instead of indexing __all__ directly.
        firstError(err) {
            if (!err) return 'An unexpected error occurred.'
            // 401s are handled globally (logged-out modal or login redirect via
            // api.js); never surface them as a per-call error toast.
            if (err.__status__ === 401) return ''
            if (Array.isArray(err)) return err[0] || 'An unexpected error occurred.'
            if (Array.isArray(err.__all__)) return err.__all__[0] || 'An unexpected error occurred.'
            if (typeof err === 'string') return err
            return err.message || 'An unexpected error occurred.'
        },
        showError(title, text) {
            if (title instanceof Array) {
                title.forEach(t => {
                    this.showError(t)
                })

                return
            }

            text = text ? text.trim() : ''
            title = title ? title.trim() : ''

            if (title && text) {
                toastr.error(text, title)
            }
            else if (title) {
                toastr.error(title)
            }
        },
        showInfo(title, text) {
            if (title instanceof Array) {
                title.forEach(t => {
                    this.showInfo(t)
                })

                return
            }

            text = text ? text.trim() : ''
            title = title ? title.trim() : ''

            if (title && text) {
                toastr.success(text, title)
            }
            else if (title) {
                toastr.success(title)
            }
        },
        showWarning(title, text) {
            text = text ? text.trim() : ''
            title = title ? title.trim() : ''

            if (title && text) {
                toastr.warning(text, title)
            }
            else if (title) {
                toastr.warning(title)
            }
        },
        showUpdatePrompt() {
            let title = "Please refresh"
            let text = "Your website version is a little out of date. Click here to refresh."

            toastr.info(text, title, {onclick: function() { window.location.reload() }})
        },
        connectUpdatesAPI() {
            if (!UPDATES_API_BASE) {return}
            if (!this.updatesSocket) {
                this.clientSocketConnected = false
                this.updatesSocket = openSocket(UPDATES_API_BASE)
                this.updatesSocket.on('connect', () => {
                    this.clientSocketConnected = true
                })

                this.updatesSocket.off('updated')
                this.updatesSocket.on('updated', (msg, cb) => {
                    this.$emit('client_data_updated', msg)
                    if (cb) {cb()}
                })
            }
        },
        disconnectUpdatesAPI() {
            if (!UPDATES_API_BASE) {return}
            if (this.updatesSocket) {
                this.updatesSocket.close()
                this.updatesSocket = null
            }
        },
        subscribeToClientUpdates(clientId) {
            if (!UPDATES_API_BASE) {return}
            clientId = `${import.meta.env.VITE_UPDATES_API_REALM}-${clientId}`
            if ((this.updatesClientId == clientId) && this.clientRoomJoined) {
                return
            }

            this.updatesClientId = clientId

            this.connectUpdatesAPI()
            if (this.clientSocketConnected) {
                this.updatesSocket.emit('join', {client_id: this.updatesClientId})
                this.clientRoomJoined = true
            }
            else {
                this.updatesSocket.on('connect', () => {
                    this.updatesSocket.emit('join', {client_id: this.updatesClientId})
                    this.clientRoomJoined = true
                })
            }
        },
        unsubscribeFromClientUpdates() {
            if (!UPDATES_API_BASE) {return}
            if (this.updatesSocket) {
                this.updatesSocket.emit('leave', {client_id: this.updatesClientId})
            }
        },
        trackEvent(event, eventData={}) {
            if (import.meta.env.VITE_MIXPANEL_ENABLED) {
                try {
                    mixpanel.track(event, eventData)
                }
                catch (ex) {
                    // ignore
                }
            }
        },
        copyToClipboard(s) {
            navigator.clipboard.writeText(s)
            this.showInfo('Copied to clipboard.')
        },
}
